import React, { FC, useMemo } from 'react';
import ReactMap, { Layer, Source } from 'react-map-gl';
import { mapBounds } from 'router/subrouters/ListingCreationWizard/components/Map/Map.component';
import circle from '@turf/circle';
import useMatchMedia from 'hooks/useMatchMedia';
import { MapValue } from 'models/Listing';
import mapStyles from 'router/subrouters/ListingCreationWizard/components/Map/mapStyles';
import { MAP_MAPBOX_STYLES } from 'data/global';

import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.styles.scss';
import './Map.styles.responsive.scss';

const BORDER_MIN_ZOOM_LEVEL = 4;
const BORDER_ZOOM_LEVEL = 5.5;
const DEFAULT_ZOOM = 4.5;
const DEFAULT_ZOOM_MOBILE = 4;
const CIRCLE_RADIUS_IN_KM = 97;

type MapProps = { location: MapValue };

const Map: FC<MapProps> = ({ location }) => {
  const { latitude, longitude } = location || {};
  const isMobile = useMatchMedia('isMobile');
  const isValidLocation = !!latitude && !!longitude;

  const center = useMemo(() => [longitude, latitude], [latitude, longitude]);

  const blueCircle = useMemo(
    () =>
      isValidLocation
        ? circle(center, CIRCLE_RADIUS_IN_KM, { units: 'kilometers' })
        : null,
    [center, isValidLocation],
  );

  return (
    <div className="business-teaser-map">
      <ReactMap
        initialViewState={{
          longitude,
          latitude,
          zoom: isMobile ? DEFAULT_ZOOM_MOBILE : DEFAULT_ZOOM,
        }}
        doubleClickZoom={false}
        maxBounds={mapBounds}
        mapStyle={MAP_MAPBOX_STYLES}
      >
        {blueCircle && (
          <Source id="circle-fill" type="geojson" data={blueCircle}>
            <Layer
              type="fill"
              source="circle-fill"
              paint={mapStyles.blueCircle}
              maxzoom={BORDER_ZOOM_LEVEL}
              minzoom={BORDER_MIN_ZOOM_LEVEL}
            />
            <Layer
              type="line"
              source="circle-fill"
              paint={mapStyles.blueOutline}
              maxzoom={BORDER_ZOOM_LEVEL}
              minzoom={BORDER_MIN_ZOOM_LEVEL}
            />
          </Source>
        )}
      </ReactMap>
    </div>
  );
};

export default Map;
